import React from "react";
import { FileText, ExternalLink } from "lucide-react";
import { PageHero, PageBody } from "./_shared";

/**
 * Shared PDF list page used by Important Links, Notices, Admissions.
 *
 * Props:
 *  - eyebrow, title, accent — for hero
 *  - items: [{id, name, pdf_url}]
 *  - emptyMsg: empty-state copy
 *  - testid
 */
export default function PdfListPage({ eyebrow, title, accent, items, emptyMsg, testid }) {
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const isNew = (it) => {
    const ts = it.created_at ? new Date(it.created_at).getTime() : NaN;
    return Number.isFinite(ts) && ts >= sevenDaysAgo;
  };
  return (
    <div data-testid={testid}>
      <PageHero eyebrow={eyebrow} title={title} accent={accent} />
      <PageBody>
        {items.length === 0 ? (
          <div className="text-center bg-white rounded-xl shadow p-12 text-slate-500">
            {emptyMsg || "Nothing posted yet."}
          </div>
        ) : (
          <ul className="grid md:grid-cols-2 gap-3">
            {items.map((it) => (
              <li
                key={it.id}
                data-testid={`${testid}-item-${it.id}`}
                className="bg-white rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-shadow flex items-center gap-3 p-4"
              >
                <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#FDC700] to-[#FFB300] text-white flex items-center justify-center shrink-0">
                  <FileText size={18} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                    <span className="truncate">{it.name}</span>
                    {isNew(it) && (
                      <span
                        data-testid={`${testid}-new-badge-${it.id}`}
                        className="shrink-0 inline-flex items-center text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-full bg-rose-500 text-white animate-pulse"
                      >
                        New
                      </span>
                    )}
                  </div>
                </div>
                {(it.pdf_url || it.external_url) ? (
                  <a
                    href={it.pdf_url || it.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-[#003C6C] hover:text-[#003C6C] font-medium"
                  >
                    Open <ExternalLink size={11} strokeWidth={1.5} />
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </PageBody>
    </div>
  );
}
